import React, { useState, useEffect } from 'react';

const TripActivityPanel = ({ messages, authToken, isLoading }) => {
  const [steps, setSteps] = useState([]);
  const [tripSummary, setTripSummary] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState(0);

  useEffect(() => {
    const extracted = extractTripData(messages);
    setSteps(extracted.steps);
    setTripSummary(extracted.summary);
  }, [messages]);

  // Cycle through loading phases while Lauren is thinking
  useEffect(() => {
    if (!isLoading) {
      // Small delay before clearing to avoid flicker
      const cleanup = setTimeout(() => setLoadingPhase(0), 300);
      return () => clearTimeout(cleanup);
    }
    setLoadingPhase(1);
    const t2 = setTimeout(() => setLoadingPhase(2), 1200);
    const t3 = setTimeout(() => setLoadingPhase(3), 2800);
    const t4 = setTimeout(() => setLoadingPhase(4), 4500);
    return () => { clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [isLoading]);

  const extractTripData = (msgs) => {
    const steps = [];
    let destinations = new Set();
    let flights = [];
    let hotels = [];
    let totalCost = 0;

    msgs.forEach((msg) => {
      if (msg.sender !== 'lauren') return;
      const text = msg.message || '';

      const destMatch = text.match(/(?:trip to|visiting|destination[s]?:?\s*)([A-Z][a-z]+(?:[\s,]+[A-Z][a-z]+)*)/g);
      if (destMatch) destMatch.forEach(d => {
        const clean = d.replace(/^(?:trip to|visiting|destination[s]?:?\s*)/i, '').trim();
        if (clean.length > 2) destinations.add(clean);
      });

      if (msg.workflow_id) {
        steps.push({ type: 'tool', label: 'Searching providers...', status: 'done', time: msg.timestamp });
      }

      if (/flight|airline|JFK|CDG|departure.*arrival/i.test(text)) {
        steps.push({ type: 'flights', label: 'Flight options found', status: 'done', time: msg.timestamp });
        const priceMatches = text.match(/\$[\d,]+/g);
        if (priceMatches) priceMatches.forEach(p => {
          const val = parseInt(p.replace(/[$,]/g, ''));
          if (val > 50 && val < 5000) flights.push(val);
        });
      }

      if (/hotel|accommodation|rating.*stars|per night/i.test(text)) {
        steps.push({ type: 'hotels', label: 'Hotel options found', status: 'done', time: msg.timestamp });
        const priceMatches = text.match(/\$[\d,]+(?:\s*per night)?/g);
        if (priceMatches) priceMatches.forEach(p => {
          const val = parseInt(p.replace(/[$,]/g, ''));
          if (val > 30 && val < 2000) hotels.push(val);
        });
      }

      if (/safety|budget breakdown|best time|weather|family suitability/i.test(text)) {
        steps.push({ type: 'analysis', label: 'Destination analyzed', status: 'done', time: msg.timestamp });
      }

      if (/day\s*\d|itinerary|day-by-day/i.test(text)) {
        steps.push({ type: 'itinerary', label: 'Itinerary created', status: 'done', time: msg.timestamp });
      }

      const totalMatch = text.match(/(?:total|grand total)[:\s]*\$[\d,]+/i);
      if (totalMatch) {
        totalCost = parseInt(totalMatch[0].replace(/[^0-9]/g, ''));
      }
    });

    let summary = null;
    if (destinations.size > 0 || flights.length > 0 || hotels.length > 0) {
      summary = {
        destinations: [...destinations],
        flightRange: flights.length ? `$${Math.min(...flights)} - $${Math.max(...flights)}` : null,
        hotelRange: hotels.length ? `$${Math.min(...hotels)} - $${Math.max(...hotels)}/night` : null,
        totalCost: totalCost || null,
      };
    }

    const seen = new Set();
    const unique = steps.filter(s => {
      if (seen.has(s.type)) return false;
      seen.add(s.type);
      return true;
    });

    return { steps: unique, summary };
  };

  const stepIcons = {
    tool: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    flights: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>,
    hotels: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    analysis: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    itinerary: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
  };

  const hasContent = steps.length > 0 || tripSummary;

  // Loading phase definitions
  const loadingSteps = [
    { icon: 'tool', label: 'Processing your request...' },
    { icon: 'flights', label: 'Searching travel providers...' },
    { icon: 'analysis', label: 'Analyzing destinations...' },
    { icon: 'hotels', label: 'Comparing options...' },
  ];

  return (
    <div data-testid="trip-activity-panel" className="trip-activity-panel">
      <div className="tap-header">
        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
        <span>Trip Planner</span>
        {isLoading && <span className="tap-live-badge" data-testid="tap-live-badge">LIVE</span>}
      </div>

      {/* Loading progress steps */}
      {isLoading && (
        <div className="tap-loading" data-testid="tap-loading-progress">
          <div className="tap-loading-title">Lauren is working...</div>
          <div className="tap-loading-steps">
            {loadingSteps.map((step, i) => {
              const phase = i + 1;
              const isActive = loadingPhase === phase;
              const isDone = loadingPhase > phase;
              const isPending = loadingPhase < phase;
              return (
                <div
                  key={i}
                  className={`tap-loading-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isPending ? 'pending' : ''}`}
                  data-testid={`tap-loading-step-${i}`}
                >
                  <div className="tap-loading-step-line">
                    <div className={`tap-loading-dot ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                      {isDone ? (
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      ) : isActive ? (
                        <span className="tap-loading-spinner" />
                      ) : (
                        stepIcons[step.icon]
                      )}
                    </div>
                    {i < loadingSteps.length - 1 && <div className={`tap-loading-connector ${isDone ? 'done' : ''}`} />}
                  </div>
                  <div className="tap-loading-step-text">
                    <span className={`tap-loading-label ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                      {step.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Existing content */}
      {!isLoading && !hasContent && (
        <div className="tap-empty">
          <svg className="w-10 h-10 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-sm text-slate-500">Start chatting with Lauren to see your trip plan build here in real-time.</p>
          <p className="text-xs text-slate-600 mt-2">Flight searches, hotel lookups, and destination analysis will appear as steps.</p>
        </div>
      )}

      {!isLoading && hasContent && (
        <div className="tap-content">
          {tripSummary && (
            <div className="tap-summary-card">
              <div className="tap-summary-title">Your Trip</div>
              {tripSummary.destinations.length > 0 && (
                <div className="tap-summary-row">
                  <span className="tap-label">Destinations</span>
                  <span className="tap-value">{tripSummary.destinations.join(' \u2192 ')}</span>
                </div>
              )}
              {tripSummary.flightRange && (
                <div className="tap-summary-row">
                  <span className="tap-label">Flights</span>
                  <span className="tap-value">{tripSummary.flightRange}</span>
                </div>
              )}
              {tripSummary.hotelRange && (
                <div className="tap-summary-row">
                  <span className="tap-label">Hotels</span>
                  <span className="tap-value">{tripSummary.hotelRange}</span>
                </div>
              )}
              {tripSummary.totalCost && (
                <div className="tap-summary-row tap-total">
                  <span className="tap-label">Estimated Total</span>
                  <span className="tap-value">${tripSummary.totalCost.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          <div className="tap-steps">
            <div className="tap-steps-title">Activity</div>
            {steps.map((step, i) => (
              <div key={i} className="tap-step" data-testid={`trip-step-${step.type}`}>
                <div className="tap-step-line">
                  <div className={`tap-step-dot ${step.status}`}>
                    {stepIcons[step.type] || stepIcons.tool}
                  </div>
                  {i < steps.length - 1 && <div className="tap-step-connector" />}
                </div>
                <div className="tap-step-info">
                  <span className="tap-step-label">{step.label}</span>
                  {step.time && (
                    <span className="tap-step-time">
                      {new Date(step.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripActivityPanel;
